import React from "react";
import styled from "styled-components";
import { EllipsisVertical, Square } from "lucide-react";
import { theme } from "@src/theme";
import { Menu } from "@szhsin/react-menu";
import { CustomMenuItem } from "./CustomMenuItem";
import Spinner from "./Spinner";
import Tooltip from "./Tooltip";

const Container = styled.div`
  color: #fff;
  padding-bottom: 20px;
  width: 100%;
  max-height: 350px;
  min-height: 160px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: ${theme.margins.medium};
  background-color: ${theme.colors.darkBg};
  justify-content: flex-start;
  align-content: flex-start;
  grid-auto-rows: min-content;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 250px;
  min-height: 160px;
  padding: ${theme.margins.medium};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  padding-bottom: ${theme.margins.medium};
  cursor: pointer;
  color: #ccc;

  input {
    display: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  &.checked {
    color: ${theme.colors.yellow};
  }
`;

const PillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 80px;
  max-height: 80px;
`;

const Pill = styled.div<{ dashed?: boolean }>`
  background-color: ${({ dashed }) =>
    dashed ? "transparent" : theme.colors.yellow};
  color: ${({ dashed }) => (dashed ? theme.colors.yellow : "#000")};
  border: 1px solid ${theme.colors.yellow};
  border-style: ${({ dashed }) => (dashed ? "dashed" : "solid")};
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  max-height: 15px;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.darkBg};
  overflow-y: auto;
  max-height: 250px;
  min-height: 160px;
  padding: ${theme.margins.medium};
`;
const NoContentTitle = styled.div`
  font-weight: bold;
  margin-bottom: 20px;
  color: ${theme.colors.gray};
`;

export type MultiSelectProps<T = unknown> = {
  loading?: boolean;
  options?: T[];
  selected?: T[];
  setSelected: React.Dispatch<React.SetStateAction<T[]>>;
  valueKey: keyof T;
  labelKey: keyof T;
  optionEntityName?: string;
  toggleCreateModal?: () => void;
  menuItems?: {
    label: string;
    onClick: (option: T) => void;
  }[];
  enableCreate?: boolean;
  loaderRef?: React.RefObject<HTMLElement>;
};

const MultiSelect = <T,>({
  loading,
  valueKey,
  labelKey,
  options = [],
  selected = [],
  setSelected,
  optionEntityName = "",
  toggleCreateModal,
  menuItems = [],
  enableCreate = true,
  loaderRef,
}: MultiSelectProps<T>) => {
  const toggleSelection = (option: T) => {
    setSelected((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  if (options?.length === 0 && !loading) {
    return (
      <Container>
        <NoContentContainer>
          <NoContentTitle>No results</NoContentTitle>
          {enableCreate && (
            <PillsContainer>
              <Pill dashed onClick={toggleCreateModal}>
                + New {optionEntityName}
              </Pill>
            </PillsContainer>
          )}
        </NoContentContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Grid className="scrollbar-style-2">
        {options.map((option) => (
          <CheckboxLabel
            key={String(option[valueKey])}
            className={selected.includes(option) ? "checked" : ""}
            onClick={() => toggleSelection(option)}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 10,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selected.includes(option) ? (
                <Square
                  fill={theme.colors.yellow}
                  color={theme.colors.yellow}
                />
              ) : (
                <Square />
              )}
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: 100,
                }}
              >
                <Tooltip
                  content={String(option[labelKey])}
                  tooltipId={String(option[valueKey])}
                >
                  {String(option[labelKey])}
                </Tooltip>
              </div>
            </div>
            {menuItems?.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: 10,
                }}
              >
                <Menu
                  menuStyle={{
                    backgroundColor: theme.colors.gray400,
                    color: theme.colors.gray,
                  }}
                  onClick={(e) => e.stopPropagation()}
                  menuButton={
                    <EllipsisVertical
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      role="button"
                    />
                  }
                  transition
                >
                  {menuItems.map((menuItem, index) => (
                    <CustomMenuItem
                      data-tooltip-id={`menu-item-${index}`}
                      key={index}
                      onClick={() => {
                        menuItem.onClick?.(option);
                      }}
                    >
                      <Tooltip
                        tooltipId={`menu-item-${index}`}
                        content={menuItem.label}
                      >
                        {menuItem.label}
                      </Tooltip>
                    </CustomMenuItem>
                  ))}
                </Menu>
              </div>
            )}
          </CheckboxLabel>
        ))}
        {loaderRef && (
          <div
            ref={loaderRef as React.RefObject<HTMLDivElement>}
            style={{ height: 1, width: "100%" }}
          ></div>
        )}
        {loading && (
          <div
            style={{
              minHeight: 30,
              width: "100%",
              position: "sticky",
              bottom: 0,
            }}
          >
            <Spinner small />
          </div>
        )}
      </Grid>
      <PillsContainer className="scrollbar-style-2">
        {enableCreate && (
          <Pill dashed onClick={toggleCreateModal}>
            + New {optionEntityName}
          </Pill>
        )}
        {selected.map((option) => (
          <Pill
            key={String(option[valueKey])}
            onClick={() => toggleSelection(option)}
          >
            {String(option[labelKey])}
          </Pill>
        ))}
      </PillsContainer>
    </Container>
  );
};

export default MultiSelect;
